<template>
  <div v-if="visible" class="modal-overlay" @click="closeModal">
    <div class="modal-content" @click.stop>
      <div class="modal-header">
        <div class="header-left">
          <h3 class="modal-title">Escalation Details</h3>
          <span :class="['level-badge', `level-${escalation?.escalation_level}`]">
            Level {{ escalation?.escalation_level }}
          </span>
        </div>
        <button @click="closeModal" class="modal-close">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
            <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
          </svg>
        </button>
      </div>

      <div class="modal-body" v-if="escalation">
        <!-- Basic Info -->
        <div class="info-section">
          <h4 class="section-title">Basic Information</h4>
          <div class="info-grid">
            <div class="info-item">
              <label class="info-label">Escalation ID</label>
              <div class="info-value">#{{ escalation.id }}</div>
            </div>
            <div class="info-item">
              <label class="info-label">Agent</label>
              <div class="info-value">{{ escalation.agent_name }}</div>
            </div>
            <div class="info-item">
              <label class="info-label">Customer Phone</label>
              <div class="info-value customer-phone">{{ escalation.customer_phone }}</div>
            </div>
            <div class="info-item">
              <label class="info-label">Date & Time</label>
              <div class="info-value">{{ formatDateTime(escalation.created_at) }}</div>
            </div>
            <div class="info-item">
              <label class="info-label">Total Violations</label>
              <div class="info-value">
                <span class="violations-badge">{{ escalation.total_violations }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Categories Affected -->
        <div class="info-section">
          <h4 class="section-title">Categories Affected</h4>
          <div class="category-tags">
            <span
              v-for="category in getUniqueCategories(escalation.categories_affected)"
              :key="category"
              :class="['category-tag', getCategoryClass(category)]"
            >
              {{ formatCategory(category) }}
            </span>
          </div>
        </div>

        <!-- Detected Words -->
        <div class="info-section">
          <h4 class="section-title">Detected Violations</h4>
          <div class="detected-words">
            <div
              v-for="(word, index) in escalation.detected_words"
              :key="index"
              class="word-item"
            >
              <div class="word-content">
                <span class="detected-word">{{ word.word }}</span>
                <span :class="['word-category', getCategoryClass(word.category)]">
                  {{ formatCategory(word.category) }}
                </span>
                <span class="word-type">{{ word.type }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Original Text -->
        <div class="info-section">
          <h4 class="section-title">Original Conversation Text</h4>
          <div class="original-text">
            <p>{{ escalation.original_text }}</p>
          </div>
        </div>

        <!-- Actions -->
        <div class="modal-actions">
          <button @click="closeModal" class="action-btn outline">Close</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'EscalationDetailModal',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    escalation: {
      type: Object,
      default: null
    }
  },
  methods: {
    closeModal () {
      this.$emit('close')
    },

    formatDateTime (dateStr) {
      const date = new Date(dateStr)
      return date.toLocaleString()
    },

    formatCategory (category) {
      const categoryMap = {
        insult: 'Insults',
        profanity: 'Profanity',
        legal_threat: 'Legal Threats',
        social_media_threat: 'Social Media Threats',
        reputation_threat: 'Reputation Threats'
      }
      return categoryMap[category] || category
    },

    getCategoryClass (category) {
      const classMap = {
        insult: 'insult',
        profanity: 'profanity',
        legal_threat: 'legal',
        social_media_threat: 'social',
        reputation_threat: 'reputation'
      }
      return classMap[category] || 'default'
    },

    getUniqueCategories (categories) {
      if (Array.isArray(categories)) {
        return categories
      }

      // Handle object format like {"0": "insult", "2": "legal_threat"}
      if (typeof categories === 'object' && categories !== null) {
        return Object.values(categories)
      }

      return []
    }

  }
}
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  backdrop-filter: blur(4px);
}

.modal-content {
  background: var(--bg-primary);
  border-radius: 16px;
  border: 1px solid var(--border-primary);
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
  max-width: 800px;
  width: 90%;
  max-height: 90vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 24px;
  border-bottom: 1px solid var(--border-primary);
}

.header-left {
  display: flex;
  align-items: center;
  gap: 12px;
}

.modal-title {
  font-size: 20px;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0;
}

.level-badge {
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  color: white;
}

.level-badge.level-1 { background: #10b981; }
.level-badge.level-2 { background: #f59e0b; }
.level-badge.level-3 { background: #ef4444; }
.level-badge.level-4 { background: #dc2626; }

.modal-close {
  background: none;
  border: none;
  color: var(--text-secondary);
  cursor: pointer;
  padding: 8px;
  border-radius: 6px;
  transition: all 0.2s;
}

.modal-close:hover {
  background: var(--bg-secondary);
  color: var(--text-primary);
}

.modal-body {
  flex: 1;
  overflow-y: auto;
  padding: 24px;
}

.info-section {
  margin-bottom: 32px;
}

.section-title {
  font-size: 16px;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0 0 16px;
}

.info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 20px;
}

.info-item {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.info-label {
  font-size: 13px;
  font-weight: 500;
  color: var(--text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.info-value {
  font-size: 14px;
  color: var(--text-primary);
  font-weight: 500;
}

.customer-phone {
  font-family: monospace;
  background: var(--bg-secondary);
  padding: 4px 8px;
  border-radius: 4px;
  display: inline-block;
}

.violations-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 24px;
  height: 24px;
  padding: 0 8px;
  background: #f59e0b;
  color: white;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
}

.category-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.category-tag {
  padding: 6px 12px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 500;
  color: white;
}

.category-tag.insult { background: #ef4444; }
.category-tag.profanity { background: #dc2626; }
.category-tag.legal { background: #7c3aed; }
.category-tag.social { background: #3b82f6; }
.category-tag.reputation { background: #f59e0b; }
.category-tag.default { background: #6b7280; }

.detected-words {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.word-item {
  padding: 16px;
  background: var(--bg-secondary);
  border-radius: 8px;
  border: 1px solid var(--border-primary);
}

.word-content {
  display: flex;
  align-items: center;
  gap: 12px;
}

.detected-word {
  font-family: monospace;
  font-size: 14px;
  font-weight: 600;
  color: var(--text-primary);
  background: var(--bg-tertiary);
  padding: 4px 8px;
  border-radius: 4px;
  direction: rtl;
  text-align: right;
}

.word-category {
  padding: 2px 6px;
  border-radius: 3px;
  font-size: 11px;
  font-weight: 500;
  color: white;
}

.word-category.insult { background: #ef4444; }
.word-category.profanity { background: #dc2626; }
.word-category.legal { background: #7c3aed; }
.word-category.social { background: #3b82f6; }
.word-category.reputation { background: #f59e0b; }
.word-category.default { background: #6b7280; }

.word-type {
  font-size: 12px;
  color: var(--text-secondary);
  background: var(--bg-primary);
  padding: 2px 6px;
  border-radius: 3px;
  border: 1px solid var(--border-primary);
}

.original-text {
  padding: 20px;
  background: var(--bg-secondary);
  border-radius: 8px;
  border: 1px solid var(--border-primary);
  max-height: 200px;
  overflow-y: auto;
}

.original-text p {
  font-size: 14px;
  line-height: 1.6;
  color: var(--text-primary);
  margin: 0;
  direction: rtl;
  text-align: right;
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
}

.modal-actions {
  display: flex;
  justify-content: center;
  margin-top: 32px;
  padding-top: 20px;
  border-top: 1px solid var(--border-primary);
}

.action-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 20px;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.action-btn.primary {
  background: #3b82f6;
  color: white;
}

.action-btn.primary:hover {
  background: #2563eb;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
}

.action-btn.outline {
  background: var(--bg-secondary);
  color: var(--text-primary);
  border: 1px solid var(--border-primary);
}

.action-btn.outline:hover {
  background: var(--bg-tertiary);
  border-color: var(--border-secondary);
}

/* Dark mode specific styles */
.dark .detected-word {
  background: var(--bg-primary);
  border: 1px solid var(--border-primary);
}

.dark .word-type {
  background: var(--bg-secondary);
}

@media (max-width: 768px) {
  .modal-content {
    width: 95%;
    max-height: 95vh;
  }

  .modal-header,
  .modal-body {
    padding: 16px;
  }

  .header-left {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }

  .info-grid {
    grid-template-columns: 1fr;
    gap: 16px;
  }

  .word-content {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }

  .modal-actions {
    flex-direction: column;
  }

  .action-btn {
    width: 100%;
    justify-content: center;
  }
}
</style>
